import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import treatmentPages from "../data/treatmentPages";
import { BRAND } from "../data/mock";

const sectionClass = "mb-8 rounded-xl border border-emerald-100 bg-white p-6 shadow-sm";

const remedyParagraph = (remedy, disease) => (
  `${remedy} is commonly considered in homeopathic practice for ${disease.toLowerCase()} when the patient presents a characteristic pattern of symptoms rather than diagnosis alone. ` +
  `At JBS Homoeopathy Clinic, this remedy is selected only after detailed case analysis, including symptom location, modalities, emotional profile, sleep pattern, and recurrent trigger factors. ` +
  `Patients who are prescribed ${remedy} may report patterns such as periodic flare-ups, constitutional sensitivity, and aggravation with specific weather, food, stress, or routine disruption. ` +
  `The medicine is planned with individualized potency and repetition under medical supervision, and response is reviewed at follow-up visits. ` +
  `Like all classical homeopathic prescriptions, ${remedy} is not given as a generic cure but as part of a personalized protocol designed to improve symptom control, reduce recurrence, and support long-term systemic balance.`
);

const faqList = (disease, remedies) => [
  {
    q: `Can homeopathy cure ${disease.toLowerCase()} permanently?`,
    a: `Homeopathy aims for long-term control by treating individual susceptibility, trigger response, and constitutional pattern in ${disease.toLowerCase()}. Outcomes vary by disease duration, severity, lifestyle, and adherence to follow-up.`,
  },
  {
    q: `Is homeopathy safe for ${disease.toLowerCase()}?`,
    a: "When prescribed by a qualified physician, homeopathic medicines are generally well tolerated. The treatment plan is individualized and monitored regularly for safety and progress.",
  },
  {
    q: "How long does treatment take?",
    a: "Acute symptoms may improve early, but chronic conditions usually need sustained treatment. Duration depends on chronicity, associated medical factors, and patient response during follow-up.",
  },
  {
    q: `What triggers ${disease.toLowerCase()}?`,
    a: `Common triggers include stress, sleep disturbance, dietary errors, environmental exposure, immune changes, and hormonal variations. Trigger mapping is part of your case-taking process.`,
  },
  {
    q: `Is ${disease.toLowerCase()} hereditary?`,
    a: "Family tendency may exist in many chronic conditions. However, severity and recurrence are also influenced by immunity, metabolism, lifestyle, emotional health, and environmental factors.",
  },
  {
    q: "Can stress worsen symptoms?",
    a: "Yes. Stress can aggravate inflammatory, hormonal, skin, respiratory, and neurological conditions. Integrative care at the clinic includes stress and sleep guidance with remedy support.",
  },
  {
    q: "Which remedies are commonly used?",
    a: `Frequently considered remedies include ${remedies.join(", ")}, chosen only after individualized assessment. Self-medication is discouraged, especially for persistent or recurrent disease patterns.`,
  },
  {
    q: "When should I consult a homeopath?",
    a: "Consult early when symptoms recur, affect quality of life, or require long-term management. Early case assessment improves planning, prevention of progression, and treatment compliance.",
  },
];

const tocItems = (disease, page) => [
  { id: "what-is", label: `What is ${disease}?` },
  { id: "symptoms", label: "Symptoms" },
  { id: "causes", label: "Causes" },
  ...(page.additionalSections || []).map(({ id, heading }) => ({ id, label: heading })),
  { id: "how-homeopathy-helps", label: page.homeopathyHeading || "How Homeopathy Treats It" },
  ...(page.showRemedies === false ? [] : [{ id: "remedies", label: "Common Homeopathic Remedies" }]),
  ...(page.showTreatmentProcess === false ? [] : [{ id: "treatment-process", label: "Treatment Process" }]),
  ...(page.showBenefits === false ? [] : [{ id: "benefits", label: "Benefits of Homeopathy" }]),
  { id: "faq", label: "FAQ" },
  { id: "book-consultation", label: "Book Consultation" },
];

const getFallbackRelated = (currentPage) => Object.values(treatmentPages)
  .filter((item) => item.category === currentPage.category && item.slug !== currentPage.slug)
  .slice(0, 4);

function TreatmentPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const page = treatmentPages[slug];
  const diseaseName = page?.disease || page?.condition || "Condition";

  const relatedPages = useMemo(() => {
    if (!page) return [];

    const mappedRelated = (page.related || [])
      .map((relatedSlug) => treatmentPages[relatedSlug])
      .filter((item) => item && item.slug !== page.slug);

    if (mappedRelated.length >= 3) {
      return mappedRelated.slice(0, 4);
    }

    const fallback = getFallbackRelated(page);
    const mergedMap = new Map();

    [...mappedRelated, ...fallback].forEach((item) => {
      if (!mergedMap.has(item.slug)) {
        mergedMap.set(item.slug, item);
      }
    });

    return Array.from(mergedMap.values()).slice(0, 4);
  }, [page]);

  const faqItems = useMemo(() => {
    if (!page) return [];
    return page.faqs || faqList(diseaseName, page.remedies || []);
  }, [page, diseaseName]);

  useEffect(() => {
    if (!page) return;

    const title = page.pageTitle || `Homeopathy Treatment for ${diseaseName} | JBS Homoeopathy Clinic`;
    const description = page.metaDescription || `Get personalized homeopathy treatment for ${diseaseName.toLowerCase()} at JBS Homoeopathy Clinic, Noida with expert care, remedies, and long-term management.`;

    document.title = title;

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }
    descriptionTag.setAttribute("content", description.slice(0, 160));

    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "MedicalCondition",
          name: diseaseName,
          description: `Homeopathic management information for ${diseaseName}`,
          possibleTreatment: {
            "@type": "MedicalTherapy",
            name: "Classical Homeopathy",
          },
        },
        {
          "@type": "Physician",
          name: BRAND.doctorName,
          medicalSpecialty: "Homeopathic",
          url: "https://jbshomoeopathy.com",
          worksFor: {
            "@type": "MedicalClinic",
            name: "JBS Homoeopathy",
          },
        },
        {
          "@type": "LocalBusiness",
          name: "JBS Homoeopathy",
          address: BRAND.address.full,
          areaServed: "Noida",
          url: "https://jbshomoeopathy.com",
          image: "https://jbshomoeopathy.com/logo.png",
          telephone: BRAND.doctorPhone,
          potentialAction: {
            "@type": "ReserveAction",
            target: "https://jbshomoeopathy.com/book",
          },
        },
        {
          "@type": "LocalBusiness",
          name: "JBS Homoeopathy - Sector 116 Clinic",
          address: BRAND.clinics[1].full,
          areaServed: "Noida",
          url: BRAND.clinics[1].mapUrl,
          telephone: BRAND.doctorPhone,
        },
        {
          "@type": "FAQPage",
          mainEntity: faqItems.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.a,
            },
          })),
        },
      ],
    };

    const existing = document.getElementById("treatment-schema");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "treatment-schema";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const schemaTag = document.getElementById("treatment-schema");
      if (schemaTag) schemaTag.remove();
    };
  }, [page, faqItems, diseaseName]);

  if (!page) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <h1 className="mb-4 text-3xl font-bold">Page not found</h1>
        <p className="mb-6 text-gray-700">The requested treatment page is unavailable.</p>
        <Link to="/services" className="rounded-md bg-emerald-600 px-4 py-2 text-white">
          Back to Services
        </Link>
      </div>
    );
  }

  const toc = tocItems(diseaseName, page);
  const remediesText = page.remedies.join(", ");

  return (
    <article className="mx-auto max-w-5xl px-4 py-10 text-left text-gray-800">
      <header className="mb-8 rounded-xl bg-emerald-50 p-8">
        <h1 className="text-4xl font-bold text-gray-900">{page.pageTitle || `Homeopathy Treatment for ${diseaseName}`}</h1>
      </header>

      <section className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">Table of Contents</h2>
        <ul className="grid gap-2 text-emerald-700 md:grid-cols-2">
          {toc.map((item) => (
            <li key={item.id}>
              <a href={`#${item.id}`} className="underline">{item.label}</a>
            </li>
          ))}
        </ul>
      </section>

      <section id="what-is" className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">What is {diseaseName}?</h2>
        <p className="mb-4 leading-7">{page.whatIs}</p>
        {page.intro && <p className="mb-4 leading-7">{page.intro}</p>}
        {!page.intro && <p className="mb-4 leading-7">Patients searching for <strong>{page.keyword}</strong> are usually looking for lasting symptom control, fewer flare-ups, and a safe long-term plan. At JBS Homoeopathy Clinic, treatment starts with understanding not only the diagnosis but also how the disease behaves in your body: timing, modalities, trigger profile, recurrence pattern, sleep quality, stress load, and constitutional tendencies. This helps us choose a remedy protocol that is individualized and clinically meaningful.</p>}
        {!page.intro && <><p className="mb-4 leading-7">Homeopathy approaches chronic disease through a whole-person model. Instead of symptom suppression alone, the focus is on improving immune balance, reducing sensitivity to triggers, and strengthening overall adaptive response. Many patients benefit when treatment is combined with lifestyle correction, sleep optimization, trigger avoidance, and regular medical follow-up. We also coordinate with existing conventional care whenever required for safe integrative management.</p>
        <p className="leading-7">Your first consultation includes detailed case-taking, previous report review, and objective treatment planning. The aim is to set realistic expectations, improve quality of life, and monitor changes scientifically over time.</p></>}
        <div className="mt-6">
          <a href="https://jbshomoeopathy.com/book" className="inline-block rounded-md bg-emerald-600 px-6 py-3 font-semibold text-white hover:bg-emerald-700">Book Consultation</a>
        </div>
      </section>

      <section id="symptoms" className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">Symptoms</h2>
        <p className="mb-4 leading-7">The clinical presentation of {diseaseName.toLowerCase()} can vary by age, disease duration, immunity, and trigger exposure. Commonly reported symptoms include:</p>
        <ul className="list-disc space-y-2 pl-6">
          {page.symptoms.map((symptom) => (
            <li key={symptom} className="leading-7">{symptom}</li>
          ))}
        </ul>
        <p className="mt-4 leading-7">Documenting frequency, intensity, and aggravating factors helps create a precise homeopathic prescription and follow-up strategy.</p>
      </section>

      <section id="causes" className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">Causes</h2>
        <p className="mb-4 leading-7">In most chronic cases, {diseaseName.toLowerCase()} develops through a combination of constitutional and environmental factors. Common causes and triggers are:</p>
        <ul className="list-disc space-y-2 pl-6">
          {page.causes.map((cause) => (
            <li key={cause} className="leading-7">{cause}</li>
          ))}
        </ul>
        <p className="mt-4 leading-7">Identifying personal triggers is essential for relapse prevention and long-term disease control.</p>
      </section>


      {(page.additionalSections || []).map((section) => (
        <section id={section.id} className={sectionClass} key={section.id}>
          <h2 className="mb-4 text-2xl font-semibold">{section.heading}</h2>
          {(section.paragraphs || []).map((paragraph) => <p className="mb-4 leading-7" key={paragraph}>{paragraph}</p>)}
          {section.bullets && <ul className="list-disc space-y-2 pl-6 leading-7">{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
        </section>
      ))}
      <section id="how-homeopathy-helps" className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">{page.homeopathyHeading || "How Homeopathy Treats It"}</h2>
        {page.homeopathyParagraphs ? page.homeopathyParagraphs.map((paragraph) => <p className="mb-4 leading-7" key={paragraph}>{paragraph}</p>) : <><p className="mb-4 leading-7">Homeopathy for {diseaseName.toLowerCase()} is individualized. The physician correlates local symptoms with constitutional traits, stress reactivity, sleep quality, appetite profile, and disease periodicity before selecting medicine. This method helps avoid generic prescribing and increases treatment precision.</p>
        <p className="mb-4 leading-7">At our clinic, treatment is not restricted to temporary symptom relief. We monitor the trend of recurrence, severity, functional impact, and patient wellbeing through planned follow-ups. As the response stabilizes, prescriptions are adjusted to support sustained recovery and lower dependency on repetitive acute interventions.</p>
        <p className="leading-7">For patients already on conventional treatment, an integrative approach is followed. Existing medicines should only be changed by the concerned specialist, ensuring safe and responsible long-term care.</p></>}
      </section>

      {page.showRemedies !== false && <section id="remedies" className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">Common Homeopathic Remedies</h2>
        <p className="mb-6 leading-7">The following remedies are often discussed for {diseaseName.toLowerCase()}: <strong>{remediesText}</strong>. These are prescribed only after individualized consultation.</p>
        <div className="space-y-6">
          {page.remedies.map((remedy) => (
            <div key={remedy} className="rounded-lg border border-emerald-100 p-4">
              <h3 className="mb-2 text-xl font-semibold">{remedy}</h3>
              <p className="leading-7">{remedyParagraph(remedy, diseaseName)}</p>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <a href="https://jbshomoeopathy.com/book" className="inline-block rounded-md bg-emerald-600 px-6 py-3 font-semibold text-white hover:bg-emerald-700">Book Consultation</a>
        </div>
      </section>}

      {page.showTreatmentProcess !== false && <section id="treatment-process" className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">Treatment Process at JBS Homoeopathy</h2>
        <ul className="list-disc space-y-3 pl-6 leading-7">
          <li><strong>Consultation:</strong> Detailed history of complaints, triggers, reports, medical background, and lifestyle profile.</li>
          <li><strong>Case analysis:</strong> Constitutional evaluation and remedy mapping using classical homeopathic principles.</li>
          <li><strong>Individualized medicine:</strong> Tailored prescription with specific potency, dosage schedule, and supportive advice.</li>
          <li><strong>Follow-up:</strong> Structured review of symptom trend, quality of life, recurrence, and prescription refinement.</li>
        </ul>
      </section>}

      {page.showBenefits !== false && <section id="benefits" className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">Benefits of Homeopathy</h2>
        <ul className="list-disc space-y-2 pl-6 leading-7">
          <li>Natural medicines prescribed in individualized doses.</li>
          <li>Focus on treating the root-cause pattern, not just local symptoms.</li>
          <li>Personalized treatment strategy based on your constitution.</li>
          <li>Safe for long-term supervised care in chronic conditions.</li>
          <li>Minimal side effects when prescribed by a qualified doctor.</li>
        </ul>
      </section>}

      <section className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">Homeopathy Treatment for {diseaseName} in Noida</h2>
        <p className="leading-7">JBS Homoeopathy Clinic provides personalized homeopathic treatment for patients in Noida and nearby areas including Greater Noida, Ghaziabad, and Delhi NCR. We combine detailed case-taking, disease-focused monitoring, and patient education for long-term recovery planning. If you are searching for trusted care in Noida for {diseaseName.toLowerCase()}, our clinic offers both in-person and online consultation pathways.</p>
      </section>

      {page.showImages !== false && <section className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">Disease Images</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <img src={`https://picsum.photos/seed/${page.slug}-1/500/320`} alt={`${diseaseName} clinical consultation`} className="h-52 w-full rounded-lg object-cover" />
          <img src={`https://picsum.photos/seed/${page.slug}-2/500/320`} alt={`${diseaseName} homeopathic treatment planning`} className="h-52 w-full rounded-lg object-cover" />
          <img src={`https://picsum.photos/seed/${page.slug}-3/500/320`} alt={`${diseaseName} patient recovery support`} className="h-52 w-full rounded-lg object-cover" />
        </div>
      </section>}

      <section id="faq" className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">FAQ</h2>
        <div className="space-y-5">
          {faqItems.map((item) => (
            <div key={item.q}>
              <h3 className="mb-2 text-lg font-semibold">{item.q}</h3>
              <p className="leading-7">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {relatedPages.length > 0 && <section className={sectionClass}>
        <h2 className="mb-4 text-2xl font-semibold">Related Conditions</h2>
        <p className="mb-4 leading-7">Explore these related condition pages to understand symptom overlap, trigger patterns, and holistic treatment planning:</p>
        <ul className="list-disc space-y-2 pl-6">
          {relatedPages.map((related) => (
            <li key={related.slug}>
              <Link className="text-emerald-700 underline" to={related.url}>
                Homeopathy Treatment for {related.disease || related.keyword}
              </Link>
            </li>
          ))}
        </ul>
      </section>}

      <section id="book-consultation" className="rounded-lg border border-emerald-200 bg-emerald-50 p-8 text-center">
        <h2 className="mb-3 text-2xl font-bold text-gray-900">Book Consultation</h2>
        <p className="mb-6 leading-7">Get a personalized treatment plan from our experienced homeopathic team.</p>
        <Link
          to="/book"
          className="inline-block rounded-md bg-emerald-600 px-8 py-3 font-semibold text-white transition hover:bg-emerald-700"
        >
          Book Consultation
        </Link>
      </section>

      <section className="mt-8 rounded-lg border border-amber-200 bg-amber-50 p-5">
        <h2 className="mb-2 text-xl font-semibold">Medical Disclaimer</h2>
        <p className="text-sm leading-6 text-gray-700">This content is for educational purposes only and does not replace medical diagnosis, emergency care, or specialist advice. Results vary from person to person. Please consult a qualified healthcare professional for individualized diagnosis and treatment. Do not discontinue prescribed medicines without your treating doctor's guidance.</p>
      </section>
    </article>
  );
}

export default TreatmentPage;
